import React, { useEffect, useRef, useState } from 'react';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import KendoReactDropDownTree from '@Shared.Angular/kendo.react/KendoReactDropDownTree';
import { getService } from '@Client/runner.hooks/useService';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';

type Props = {
  onClose: () => void;
};

const CreateProcedureModal = ({ onClose }: Props) => {
  const APP_CONFIG = getService('APP_CONFIG');
  const [procedureName, setProcedureName] = useState('');
  const [procedureCategoryId, setProcedureCategoryId] = useState(null);
  const [categories, setCategories] = useState<ICategoryDetail[]>(null);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const procedureNameInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getService('categoryApiService')
      .getCachedCategories()
      .then((categoryDetails) => {
        setCategories(categoryDetails);
      });
  }, []);

  useEffect(() => {
    if (categories !== null) {
      procedureNameInput.current?.focus();
    }
  }, [categories]);

  const handleStartRecording = async (aiEnhance: boolean) => {
    if (!procedureName || isRequestPending) return;

    setIsRequestPending(true);

    getService('procedureApiService')
      .createProcedure({ name: procedureName, categoryId: procedureCategoryId })
      .then((procedureId) => {
        getService('proceduresService').startExtensionProcedureRecording(
          procedureId,
          procedureName,
          aiEnhance
        );
        setIsRequestPending(false);
        onClose();
      })
      .catch(() => {
        getService('notificationService').showErrorToast(
          'Failed to start recording'
        );
        setIsRequestPending(false);
      });
  };

  if (categories === null) return null;

  return (
    <Modal
      title="Record new SOP"
      onClose={onClose}
      onCancelClick={onClose}
      actionButtonText="Begin recording"
      onActionClick={() => handleStartRecording(false)}
      secondaryButtonText={
        APP_CONFIG.enableProcedureAiEnhancement
          ? 'Begin recording with AI'
          : undefined
      }
      onSecondaryButtonClick={() => handleStartRecording(true)}
      size="md"
      id="create-procedure-dialog"
    >
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <div className="content">
        <div className="input-field col s12">
          SOP Name
          <input
            id="record-procedure-name"
            value={procedureName}
            onChange={(e) => setProcedureName(e.target.value)}
            type="text"
            ref={procedureNameInput}
          />
        </div>
        {categories.length > 0 && (
          <div className="input-field col s12">
            SOP Category
            <KendoReactDropDownTree
              id="create-procedure-category-dropdown"
              categories={categories}
              categoryClicked={setProcedureCategoryId}
              placeholder="Select a category"
              selectedId={procedureCategoryId}
              dataItemKey="id"
              textField="name"
              subItemsField="subCategories"
              defaultItemId={null}
              showFilter={true}
            />
          </div>
        )}
        <div>
          Once you click 'Begin recording' your actions in the browser will be
          automatically recorded as part of this new SOP. To end recording click
          'Stop' in the recording icon that will appear in the bottom left of
          the browser window.
        </div>
      </div>
    </Modal>
  );
};

export default CreateProcedureModal;
